import { useGetCurrentUserQuery } from '_common/services/api/authority';
import { Select, SelectProps, SelectOption } from '@ets/design-system';
import { SingleValue, ActionMeta } from 'react-select';
import { useCallback, useEffect, useMemo, useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';

export type TimepickerProps = Omit<SelectProps<SelectOption, false>, 'timeFormat' | 'onChange'> & {
  onChange?: (
    isValid: boolean,
    value: SingleValue<SelectOption>,
    actionMeta?: ActionMeta<SelectOption>,
  ) => void;
  timeInterval: number;
};

const Timepicker = ({ timeInterval = 30, ...props }) => {
  const { data: currentUser } = useGetCurrentUserQuery();
  const [valueIsValid, setValueIsValid] = useState<boolean>(true);
  const [selectedOption, setSelectedOption] = useState<SelectOption | null>();
  const timeFormat = currentUser?.profile.time_format;

  const format = useMemo(() => {
    return timeFormat === '12h' ? 'hh:mm A' : 'HH:mm';
  }, [timeFormat]);

  const getTimeLabel = useCallback(
    (timeLabel: string | Dayjs) => {
      const date =
        timeLabel instanceof dayjs
          ? (timeLabel as Dayjs)
          : dayjs(`1/1/1 ${timeLabel}`, 'D/M/Y HH:mm');
      if (date.isValid()) {
        return date.format(format);
      }

      return null;
    },
    [format],
  );

  /* 
  /* The value of the options will be set always based on the 24h format
  /* This way we will be able to convert options nicely
  */
  const getTimeValue = useCallback((time: string | Dayjs) => {
    if (time && typeof time === 'string') {
      return dayjs(`1/1/1 ${time}`, 'D/M/Y HH:mm').format('HH:mm');
    } else if (time instanceof dayjs) {
      return (time as Dayjs).format('HH:mm');
    }
    return null;
  }, []);

  const options = useMemo(() => {
    const options = [];

    let hour = 0;
    let minute = 0;
    let date: dayjs.Dayjs | null = null;

    const interval = Math.min(60, Math.max(1, timeInterval));

    for (hour = 0; hour < 24; hour++) {
      for (minute = 0; minute < 60; minute += interval || 1) {
        date = dayjs(`1/1/1 ${hour}:${minute}`);
        options.push({
          value: getTimeValue(date) || '',
          label: getTimeLabel(date) || '',
        });
      }
    }

    return options;
  }, [timeInterval, getTimeLabel, getTimeValue]);

  const getTimeOption = useCallback(
    (time: string | Dayjs) => {
      if (time) {
        return { value: getTimeValue(time) || '', label: getTimeLabel(time) || '' };
      }
      return null;
    },
    [getTimeValue, getTimeLabel],
  );

  const onInputChange = (
    newValue: string,
    actionMeta: { action: string; prevInputValue: string },
  ) => {
    //When there is a search
    if (actionMeta.action === 'input-change') {
      /*
      /* Allow 3 digits to be written: when the colon is deleted it can be written a 3rd digit and the colon its placed automatically in between
      /* This way, when the colon is deleted there is no need to write the colon manually or rewrite the second digit
      /* Matches: [000]
      */
      if (newValue.match(/^[0-9]{3}$/)) {
        const digits = newValue.split('');
        return `${digits[0]}${digits[1]}:${digits[2]}`;
      }

      //Only allow the value of the input to be the following patterns:
      if (timeFormat === '12h') {
        //Matches: [0, 0:0, 0:00, 00:, 00:0, 00:00, 0:0 A, 0:0 AM, 0:0 P, 0:0 PM, 0:00 A, 0:00 AM, 0:00 P, 0:00 PM, 00:00 A, 00:00 P, 00:00 AM, 00:00 PM].
        if (
          !newValue.match(
            /^([0-9]{0,2})(:[0-9]{0,2}\s{0,1})?(^([0-9]{1,2})(:[0-9]{1,2})?(\s{1}([aA]|[pP])[mM]?))?$/,
          )
        ) {
          return actionMeta.prevInputValue;
        }
      } else {
        //Matches: [0, 0:0, 0:00, 00:, 00:0, 00:00].
        if (!newValue.match(/^([0-9]{0,2})(:[0-9]{0,2})?$/)) {
          return actionMeta.prevInputValue;
        }
      }

      //When Written the first 2 numbers, place a colon
      if (newValue.match(/^[0-9]{2}$/)) {
        //Only write the colon if there wasnt a colon before, need this validation to allow to delete the colon and previous digits.
        if (!actionMeta.prevInputValue.match(/^[0-9]{2}:$/)) {
          return `${newValue}:`;
        }
      }
      return newValue;
    }
  };

  //Set a default value that comes from props
  useEffect(() => {
    if (props.value) {
      const defaultOption = getTimeOption(props.value.value);
      //@ts-expect-error
      setSelectedOption({
        ...defaultOption,
      });
    } else {
      setSelectedOption(null);
    }
  }, [props.value?.value, options, getTimeOption]);

  useEffect(() => {
    setValueIsValid(dayjs(`1/1/1 ${selectedOption?.label}`).isValid());
  }, [selectedOption]);

  if (!currentUser) {
    return null;
  }

  return (
    <Select
      {...props}
      hasError={props.error === undefined ? !valueIsValid : props.error}
      isCreatable={true}
      isClearable={false}
      placeholder={currentUser.profile.time_format === '12h' ? 'HH:HH AM/PM' : 'HH:MM'}
      options={options}
      value={selectedOption}
      onChange={(newValue, actionMeta) => {
        const date = dayjs(`1/1/1 ${newValue?.label}`);
        const isValid = date.isValid();

        const newSelectedOption = {
          ...newValue,
          value: getTimeValue(date) || '',
          label: isValid ? dayjs(date).format(format) : newValue?.label,
        };

        setSelectedOption({
          ...newSelectedOption,
          label: newSelectedOption.label || '',
        });

        if (props.onChange) {
          props.onChange(isValid, newSelectedOption, actionMeta);
        }
      }}
      onInputChange={onInputChange}
      formatCreateLabel={(inputValue) => {
        const date = dayjs(`1/1/1 ${inputValue}`);

        if (!date.isValid()) {
          return inputValue;
        }

        const matchedOptions = options.filter((option) =>
          (option.value as string).includes(inputValue as string),
        );

        if (matchedOptions.length > 1) {
          return inputValue;
        }

        return getTimeLabel(inputValue) || inputValue;
      }}
      size="m"
      variant="standard"
      width="19.5rem"
    />
  );
};

export default Timepicker;
